import { useCallback } from "react";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { CheckIcon, RubleIcon } from "@/shared/icons";
import { SoundButton } from "@/shared/ui";
import { inventoryByIdSelector, setMemoizedItem } from "@/domains/inventory/slice";
import useTradeManager from "@/domains/inventory/hooks/trade/useTradeManager";
import useTradeParticipants from "@/domains/inventory/hooks/trade/useTradeParticipants";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";
import InventoryTradeOtherSuggestionHeader from "@/domains/inventory/components/trade/InventoryTradeOtherSuggestionHeader";

type Props = { currCharacterId: number };

export default function InventoryTradeOtherSuggestionContainer({ currCharacterId }: Props) {
  const dispatch = useAppDispatch();
  const { tradeParticipant, trade } = useTradeParticipants(currCharacterId);
  const inventory = useAppSelector((state) =>
    inventoryByIdSelector(state, tradeParticipant?.other?.inventory.id),
  );
  const { cancelTrade } = useTradeManager();

  const onClose = useCallback(() => {
    cancelTrade({ tradeId: Number(trade.tradeId) });
  }, [cancelTrade, trade]);

  if (!inventory || !trade) return null;

  return (
    <InventoryWrapper className="max-h-[42rem]">
      <InventoryHeader className="px-0 border border-b-0 border-dark-900 rounded-t-[inherit] overflow-clip">
        <InventoryTradeOtherSuggestionHeader
          onClose={onClose}
          name={tradeParticipant.other.name}
          id={tradeParticipant.other.characterId}
        />
      </InventoryHeader>
      <InventoryContent className="py-5 text-18 font-medium">
        Предложение обмена от игрока
      </InventoryContent>
      <InventoryContent className="flex-1 overflow-clip overflow-y-auto">
        <InventoryGrid
          inventoryId={inventory.id}
          cols={inventory.size.width}
          rows={inventory.size.height}
          items={inventory.items}
          onMemoizedItem={(item) => dispatch(setMemoizedItem(item))}
          disabled
        />
      </InventoryContent>
      <InventoryContent className="py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <RubleIcon size={18} />
          <div className="text-14 font-bold text-white/50">
            Доплата игрока:{" "}
            <span className="text-15 font-bold text-white ml-1">
              {formatPrice(tradeParticipant.other.cashOffer)} ₽
            </span>
          </div>
        </div>
        {tradeParticipant.other.isReady && (
          <div
            className={clsx(
              "flex items-center gap-2 px-2 border border-[#191919] rounded-[0.5rem]",
              "h-[2rem] text-15 font-medium",
            )}
          >
            <CheckIcon size={12} className="text-[#84FF97]" />
            Подтвердил обмен
          </div>
        )}
      </InventoryContent>
      <InventoryFooter>
        <InventoryWeight weight={tradeParticipant.other.totalWeight} />
        {tradeParticipant.other.isConfirmed && (
          <SoundButton
            data-mz-accent="success"
            variant="solid-surface-gradient"
            className="h-[2.375rem]"
            loading
            disabled
          >
            Готов
          </SoundButton>
        )}
      </InventoryFooter>
    </InventoryWrapper>
  );
}
